import { Trans, useLingui } from '@lingui/react';
import { Alert } from '@startuptools/ui/Alert';
import { Box } from '@startuptools/ui/Box';
import { Grid } from '@startuptools/ui/Grid';
import { Button } from '@startuptools/ui/Button';
import { Stack } from '@startuptools/ui/Stack';
import { Table } from '@startuptools/ui/Table';
import { Select } from '@startuptools/ui/Select';
import { Option } from '@startuptools/ui/Option';
import { Typography } from '@startuptools/ui/Typography';
import { NumberFormat } from '@startuptools/ui/NumberFormat';
import { NumericFormatInput } from '@startuptools/ui/NumericFormatInput';
import { FormControl } from '@startuptools/ui/FormControl';
import { FormLabel } from '@startuptools/ui/FormLabel';
import { FormHelperText } from '@startuptools/ui/FormHelperText';
import { Controller, type SubmitHandler, useForm } from 'react-hook-form';
import { useMemo } from 'react';
import { isEmpty, isNil } from 'lodash-es';
import { BigNumber } from 'bignumber.js';
import { GqlCurrency, GqlMeeting, GqlMeetingConcern, GqlShareIssue } from '../../../../graphql/base-types.graphql';
import { useStepNewSharesQuotaSuspenseQuery } from './ShareIssueStepper.graphql';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import { useSiSetNewSharesMutation } from '../../../../graphql/react-operations';
import { toast } from '@startuptools/ui/toast/use-toast';
import * as Sentry from '@sentry/react';
import { SipShareSubscriptionNoteListDocument } from '../../../common/components/documents/SipShareSubscriptionNoteList.graphql';
import { ShareIssueDocument } from '../../ShareIssue.graphql';
import { currenciesShort } from '../../../../components/currencies';
import React from 'react';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';

type FormValues = {
  numberOfSharesBefore: string;
  shareCapitalBefore: string;
  quotaValue: string;
  pricePerShare: string;
  currency: GqlCurrency;
  minNumberOfShares: string;
  maxNumberOfShares: string;
};

export const SiStepNewSharesContent = ({
  shareIssue,
  snrQuota,
  snrNumberOfShares,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    | 'id'
    | 'companyId'
    | 'pricePerShare'
    | 'numberOfSharesBefore'
    | 'quotaValue'
    | 'minNumberOfShares'
    | 'maxNumberOfShares'
    | 'currency'
  >;
  snrQuota: number | null | undefined;
  snrNumberOfShares: number | null | undefined;
}) => {
  const { i18n } = useLingui();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  const [doSubmit, { loading }] = useSiSetNewSharesMutation({
    refetchQueries: [ShareIssueDocument, SipShareSubscriptionNoteListDocument],
  });

  const defaultNumberOfSharesBefore = shareIssue.numberOfSharesBefore ?? `${snrNumberOfShares ?? ''}`;
  const defaultQuotaValue = shareIssue.quotaValue ?? `${snrQuota ?? ''}`;
  const shareCapitalBefore = new BigNumber(defaultQuotaValue).times(defaultNumberOfSharesBefore).toString();

  const { control, watch, handleSubmit, setValue } = useForm<FormValues>({
    defaultValues: {
      numberOfSharesBefore: defaultNumberOfSharesBefore,
      shareCapitalBefore: shareCapitalBefore,
      quotaValue: defaultQuotaValue,
      pricePerShare: shareIssue.pricePerShare ?? '',
      minNumberOfShares: shareIssue.minNumberOfShares ?? '0',
      maxNumberOfShares: shareIssue.maxNumberOfShares ?? '',
      currency: shareIssue.currency,
    },
  });

  React.useEffect(() => {
    setValue('pricePerShare', shareIssue.pricePerShare ?? '');
    setValue('quotaValue', defaultQuotaValue);
    setValue('numberOfSharesBefore', defaultNumberOfSharesBefore);
    setValue('maxNumberOfShares', shareIssue.maxNumberOfShares ?? '');
    setValue('minNumberOfShares', shareIssue.minNumberOfShares ?? '');
  }, [shareIssue, setValue, defaultQuotaValue, defaultNumberOfSharesBefore]);

  const numberOfSharesBefore = watch('numberOfSharesBefore');
  const currency = watch('currency');
  const quotaValue = watch('quotaValue');
  const minNumberOfShares = watch('minNumberOfShares');
  const maxNumberOfShares = watch('maxNumberOfShares');
  const pricePerShare = watch('pricePerShare');

  const valuationBefore = useMemo(() => {
    if (isNil(numberOfSharesBefore) || isNil(pricePerShare)) {
      return null;
    }
    if ([numberOfSharesBefore, pricePerShare].some(e => isEmpty(e))) {
      return null;
    }
    return new BigNumber(numberOfSharesBefore).times(pricePerShare).toNumber();
  }, [numberOfSharesBefore, pricePerShare]);

  // Max values
  const tableDataAtMaxSub = useMemo(() => {
    if (isNil(numberOfSharesBefore) || isNil(maxNumberOfShares) || isNil(pricePerShare)) {
      return null;
    }
    if ([numberOfSharesBefore, maxNumberOfShares, pricePerShare].some(e => isEmpty(e))) {
      return null;
    }
    const totalSharesAtMinSub = new BigNumber(numberOfSharesBefore).plus(maxNumberOfShares);
    const valuation = totalSharesAtMinSub.times(pricePerShare).toNumber();
    const raisedAmount = new BigNumber(maxNumberOfShares).times(pricePerShare).toNumber();
    const dilution = new BigNumber(maxNumberOfShares).div(totalSharesAtMinSub).toNumber();
    return {
      valuation,
      raisedAmount,
      dilution,
    };
  }, [numberOfSharesBefore, maxNumberOfShares, pricePerShare]);

  // Min values
  const tableDataAtMinSub = useMemo(() => {
    if (isNil(numberOfSharesBefore) || isNil(minNumberOfShares) || isNil(pricePerShare)) {
      return null;
    }
    if ([numberOfSharesBefore, minNumberOfShares, pricePerShare].some(e => isEmpty(e))) {
      return null;
    }
    const totalSharesAtMinSub = new BigNumber(numberOfSharesBefore).plus(minNumberOfShares);
    const valuation = totalSharesAtMinSub.times(pricePerShare).toNumber();
    const raisedAmount = new BigNumber(minNumberOfShares).times(pricePerShare).toNumber();
    const dilution = new BigNumber(minNumberOfShares).div(totalSharesAtMinSub).toNumber();
    return {
      valuation,
      raisedAmount,
      dilution,
    };
  }, [numberOfSharesBefore, minNumberOfShares, pricePerShare]);

  const onSubmit: SubmitHandler<FormValues> = async ({ shareCapitalBefore: _, ...data }) => {
    try {
      await doSubmit({ variables: { companyId: shareIssue.companyId, input: { id: shareIssue.id, ...data } } });
    } catch (e) {
      Sentry.captureException(e);
      toast({
        title: <Trans id="Error!" />,
        severity: 'danger',
        description: <Trans id="Could not save, please try again" />,
      });
      return;
    }
    return stepNext();
  };

  return (
    <Stack gap={3} marginBottom={4}>
      <Typography level="h4">
        <Trans id="How much money will be raised and to what valuation?" />
      </Typography>

      <Alert variant="outlined">
        <Trans id="Here you state how much money the company shall raise by specifying the price per share, as well as the minimum and maximum number of shares issued. The minimum number is the minimum number required for the share issue to be valid. You may also change the number of shares that the company has before the share issue, as well as the share capital. In most cases, the pre-filled values are correct, but if a share issue has been decided but has not yet been registered, you may need to adjust the values here." />
      </Alert>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={3}>
          <Grid container spacing={2} maxWidth={800} sx={{ flexGrow: 1 }}>
            <Grid xs={12} md={4}>
              <Controller
                control={control}
                name="numberOfSharesBefore"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Number of Shares Before" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      value={value ?? ''}
                      onChange={e => {
                        if (!isNil(quotaValue) && new BigNumber(e.target.value).isGreaterThan(0)) {
                          setValue('shareCapitalBefore', new BigNumber(quotaValue).times(e.target.value).toString());
                        }
                        return onChange(e);
                      }}
                      lang={i18n.locale}
                      endDecorator="st"
                      decimalScale={0}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid xs={12} md={4}>
              <Controller
                control={control}
                name="shareCapitalBefore"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, onChange, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Share Capital Before" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      value={value ?? ''}
                      onChange={e => {
                        if (numberOfSharesBefore) {
                          setValue('quotaValue', new BigNumber(e.target.value).div(numberOfSharesBefore).toString());
                        }
                        return onChange(e);
                      }}
                      lang={i18n.locale}
                      endDecorator="SEK"
                      decimalScale={6}
                    />
                  </FormControl>
                )}
              />
            </Grid>
            <Grid xs={12} md={3}>
              <Controller
                control={control}
                name="quotaValue"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Quota Value" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      value={value ?? ''}
                      lang={i18n.locale}
                      endDecorator="SEK"
                      decimalScale={6}
                      readOnly
                    />
                    <FormHelperText>
                      <Trans id="Filled Automatically" />
                    </FormHelperText>
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <Controller
                control={control}
                name="pricePerShare"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Price per Share" />
                    </FormLabel>
                    <NumericFormatInput
                      {...field}
                      value={value ?? ''}
                      lang={i18n.locale}
                      decimalScale={20}
                      endDecorator={
                        <Controller
                          control={control}
                          name="currency"
                          rules={{ required: true }}
                          render={({ field: { onChange, ...field } }) => (
                            <Select {...field} onChange={(_, v) => onChange(v ?? shareIssue.currency)} variant="plain">
                              {currenciesShort.map(c => (
                                <Option key={c.code} value={c.code}>
                                  {c.code}
                                </Option>
                              ))}
                            </Select>
                          )}
                        />
                      }
                    />
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <Controller
                control={control}
                name="minNumberOfShares"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Minimum Number of Shares" />
                    </FormLabel>
                    <NumericFormatInput
                      value={value ?? ''}
                      {...field}
                      lang={i18n.locale}
                      decimalScale={0}
                      endDecorator={<Trans id="New Shares" />}
                    />
                  </FormControl>
                )}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <Controller
                control={control}
                name="maxNumberOfShares"
                rules={{ required: true, min: 0 }}
                render={({ field: { value, ...field } }) => (
                  <FormControl required>
                    <FormLabel>
                      <Trans id="Maximum Number of Shares" />
                    </FormLabel>
                    <NumericFormatInput
                      value={value ?? ''}
                      {...field}
                      lang={i18n.locale}
                      decimalScale={0}
                      endDecorator={<Trans id="New Shares" />}
                    />
                  </FormControl>
                )}
              />
            </Grid>
          </Grid>

          <Table sx={{ maxWidth: 784 }} size="sm" variant="outlined">
            <thead>
              <tr>
                <th />
                <th>
                  <Trans id="Raised amount" />
                </th>
                <th>
                  <Trans id="Dilution" />
                </th>
                <th>
                  <Trans id="Valuation" />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th>
                  <Trans id="Before this share issue" />
                </th>
                <td />
                <td />
                <td>
                  {!isNil(valuationBefore) && (
                    <NumberFormat locale={i18n.locale} variant="currency" currency={currency ?? 'SEK'}>
                      {valuationBefore}
                    </NumberFormat>
                  )}
                </td>
              </tr>
              <tr>
                <th>
                  <Trans id="At minimum subscription" />
                </th>
                {tableDataAtMinSub ? (
                  <>
                    <td>
                      <NumberFormat locale={i18n.locale} variant="currency" currency={currency ?? 'SEK'}>
                        {tableDataAtMinSub.raisedAmount}
                      </NumberFormat>
                    </td>
                    <td width={100}>
                      <NumberFormat locale={i18n.locale} variant="percentage">
                        {tableDataAtMinSub.dilution}
                      </NumberFormat>
                    </td>
                    <td>
                      <NumberFormat locale={i18n.locale} variant="currency" currency={currency ?? 'SEK'}>
                        {tableDataAtMinSub.valuation}
                      </NumberFormat>
                    </td>
                  </>
                ) : (
                  <td colSpan={3}>
                    <Typography fontStyle="italic">
                      <Trans id="Fill out form above" />
                    </Typography>
                  </td>
                )}
              </tr>
              <tr>
                <th>
                  <Trans id="At full subscription" />
                </th>
                {tableDataAtMaxSub ? (
                  <>
                    <td>
                      <NumberFormat locale={i18n.locale} variant="currency" currency={currency ?? 'SEK'}>
                        {tableDataAtMaxSub.raisedAmount}
                      </NumberFormat>
                    </td>
                    <td>
                      <NumberFormat locale={i18n.locale} variant="percentage">
                        {tableDataAtMaxSub.dilution}
                      </NumberFormat>
                    </td>
                    <td>
                      <NumberFormat locale={i18n.locale} variant="currency" currency={currency ?? 'SEK'}>
                        {tableDataAtMaxSub.valuation}
                      </NumberFormat>
                    </td>
                  </>
                ) : (
                  <td colSpan={3}>
                    <Typography fontStyle="italic">
                      <Trans id="Fill out form above" />
                    </Typography>
                  </td>
                )}
              </tr>
            </tbody>
          </Table>

          {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

          <Box>
            <Button type="submit" loading={loading}>
              <Trans id="Save and Continue" />
            </Button>
          </Box>
        </Stack>
      </form>
    </Stack>
  );
};

export const SiStepNewShares = ({
  shareIssue,
}: {
  shareIssue: Pick<
    GqlShareIssue,
    | 'id'
    | 'companyId'
    | 'pricePerShare'
    | 'numberOfSharesBefore'
    | 'quotaValue'
    | 'minNumberOfShares'
    | 'maxNumberOfShares'
    | 'currency'
  > & {
    meetingConcern?: (Pick<GqlMeetingConcern, 'id'> & { meeting: Pick<GqlMeeting, 'id' | 'startTime'> }) | null;
  };
}) => {
  const date = useMemo(() => {
    return shareIssue.meetingConcern?.meeting.startTime ?? new Date().toString();
  }, [shareIssue]);

  const {
    data: { snrQuotaValueAtDate, snrNumberOfSharesAtDate },
  } = useStepNewSharesQuotaSuspenseQuery({
    variables: {
      companyId: shareIssue.companyId,
      date,
    },
  });

  return (
    <SiStepNewSharesContent
      shareIssue={shareIssue}
      snrNumberOfShares={snrNumberOfSharesAtDate}
      snrQuota={snrQuotaValueAtDate}
    />
  );
};
